import { useMemo } from "react";
import { IPayrollReport } from "../models/Payroll";
import { getCurrencyFormat } from "../utils/index.ts";
import { calcularTiempoTrabajado } from "../interfaces/formik/IReporteRelojChecador.ts";
import {
  calculateDailyPayment,
  getDailyPaymentConcept,
  calculateExtraHoursLikeChart,
  roundHours,
} from "../services/payroll/ui.ts";
import { formatDataForReport } from "../utils/reporteChequeoUtils.ts";
import * as moment from "moment-timezone";

interface UseCalculateTotalPayProps {
  data: IPayrollReport;
  detailedRecords?: any[];
  dates?: { start: any; end: any };
  realCheckRecords?: any[];
  useSimpleCalculation?: boolean;
  fullDayHours?: number;
}

export const useCalculateTotalPay = ({
  data,
  detailedRecords,
  dates,
  realCheckRecords,
  useSimpleCalculation = false,
  fullDayHours: fullDayHoursProp,
}: UseCalculateTotalPayProps) => {
  const fullDayHours =
    typeof fullDayHoursProp === "number" &&
    !isNaN(fullDayHoursProp) &&
    fullDayHoursProp > 0
      ? fullDayHoursProp
      : 8.5;

  const calculateRealExtraHours = useMemo(() => {
    if (realCheckRecords && realCheckRecords.length > 0 && dates) {
      const formattedData = formatDataForReport(
        realCheckRecords,
        dates.start.format("YYYY-MM-DD"),
        dates.end.format("YYYY-MM-DD")
      );

      const partnerData = formattedData.filter(
        (checkRecord) => checkRecord.checkIn?.partner_id === data.partnerId
      );

      let totalExtraHours = 0;

      for (const checkRecord of partnerData) {
        if (checkRecord.checkIn && checkRecord.checkOut) {
          const schedule = (data.realSchedule || []).find(
            (s) =>
              moment(s.day).format("YYYY-MM-DD") ===
              moment(checkRecord.checkIn.assistanceCheck_checkDate).format(
                "YYYY-MM-DD"
              )
          );

          if (schedule) {
            const entradaProgramada = schedule.start + ":00";
            const salidaProgramada = schedule.end + ":00";
            const entradaRegistrada =
              checkRecord.checkIn.assistanceCheck_checkTime;
            const salidaRegistrada =
              checkRecord.checkOut.assistanceCheck_checkTime;

            const horasRegistradas = calcularTiempoTrabajado(
              entradaRegistrada,
              salidaRegistrada
            );

            let horasTrabajadasEnHorario = 0;

            if (salidaProgramada === "00:00:00") {
              if (entradaRegistrada >= entradaProgramada) {
                horasTrabajadasEnHorario = calcularTiempoTrabajado(
                  entradaRegistrada,
                  "24:00:00"
                );
              } else if (entradaRegistrada < entradaProgramada) {
                horasTrabajadasEnHorario = calcularTiempoTrabajado(
                  entradaProgramada,
                  "24:00:00"
                );
              }
            } else {
              if (entradaRegistrada >= entradaProgramada) {
                horasTrabajadasEnHorario = calcularTiempoTrabajado(
                  entradaRegistrada,
                  salidaProgramada
                );
              } else if (
                entradaRegistrada < entradaProgramada &&
                salidaRegistrada > entradaProgramada
              ) {
                horasTrabajadasEnHorario = calcularTiempoTrabajado(
                  entradaProgramada,
                  salidaProgramada
                );
              }
            }

            let diferencia = horasRegistradas - horasTrabajadasEnHorario;

            const scheduleDate = moment(
              checkRecord.checkIn.assistanceCheck_checkDate
            ).format("YYYY-MM-DD");
            const scheduleWithIncidents = data.schedules.find(
              (s) =>
                moment(s.schedule_exactDate).format("YYYY-MM-DD") ===
                scheduleDate
            );

            if (scheduleWithIncidents && scheduleWithIncidents.incidents) {
              const hasRejectedLateCheckout =
                scheduleWithIncidents.incidents.some(
                  (incident) =>
                    incident.status === "Salió tarde" &&
                    incident.finalStatus === false
                );

              const hasRejectedEarlyCheckin =
                scheduleWithIncidents.incidents.some(
                  (incident) =>
                    incident.status === "Llegó temprano" &&
                    incident.finalStatus === false
                );

              if (hasRejectedLateCheckout || hasRejectedEarlyCheckin) {
                diferencia = 0;
              }
            }

            if (diferencia > 0) {
              totalExtraHours += Math.floor(diferencia);
            }
          }
        }
      }

      return totalExtraHours;
    }

    return calculateExtraHoursLikeChart(
      detailedRecords || [],
      data.partnerId,
      data.realSchedule as any
    );
  }, [
    realCheckRecords,
    dates?.start?.format("YYYY-MM-DD"),
    dates?.end?.format("YYYY-MM-DD"),
    data.partnerId,
    data.schedules,
    detailedRecords,
    data.realSchedule,
  ]);

  const calculateRealDailyPayments = useMemo(() => {
    if (realCheckRecords && realCheckRecords.length > 0 && dates) {
      const dailySalary = Number(String(data.dailySalary ?? "").replace(/[^0-9.-]+/g, ""));
      const hourlyPay = Number(String(data.payPerHour ?? "").replace(/[^0-9.-]+/g, ""));

      const formattedData = formatDataForReport(
        realCheckRecords,
        dates.start.format("YYYY-MM-DD"),
        dates.end.format("YYYY-MM-DD")
      );

      const partnerData = formattedData.filter(
        (checkRecord) => checkRecord.checkIn?.partner_id === data.partnerId
      );

      const payments = partnerData
        .map((checkRecord) => {
          if (!checkRecord.checkIn || !checkRecord.checkOut) {
            return null;
          }

          const checkInTime = checkRecord.checkIn.assistanceCheck_checkTime;
          const checkOutTime = checkRecord.checkOut.assistanceCheck_checkTime;
          const checkDate = checkRecord.checkIn.assistanceCheck_checkDate;

          const totalHours = calcularTiempoTrabajado(checkInTime, checkOutTime);

          const correspondingSchedule = data.schedules?.find(
            (schedule) =>
              moment(schedule.schedule_exactDate).format("YYYY-MM-DD") ===
              moment(checkDate).format("YYYY-MM-DD")
          );

          let hoursInScheduleForCalc = 0;
          const scheduleEntry = data.realSchedule?.find(
            (s) =>
              moment(s.day).format("YYYY-MM-DD") ===
              moment(checkDate).format("YYYY-MM-DD")
          );

          if (scheduleEntry) {
            const entradaProgramada = scheduleEntry.start + ":00";
            const salidaProgramada = scheduleEntry.end + ":00";
            const entradaRegistrada = checkInTime;
            const salidaRegistrada = checkOutTime;

            if (salidaProgramada === "00:00:00") {
              if (entradaRegistrada !== salidaRegistrada) {
                if (entradaRegistrada >= entradaProgramada) {
                  hoursInScheduleForCalc = calcularTiempoTrabajado(
                    entradaRegistrada,
                    "24:00:00"
                  );
                } else if (entradaRegistrada < entradaProgramada) {
                  hoursInScheduleForCalc = calcularTiempoTrabajado(
                    entradaProgramada,
                    "24:00:00"
                  );
                }
              }
            } else {
              if (entradaRegistrada !== salidaRegistrada) {
                if (entradaRegistrada >= entradaProgramada) {
                  hoursInScheduleForCalc = calcularTiempoTrabajado(
                    entradaRegistrada,
                    salidaProgramada
                  );
                } else if (
                  entradaRegistrada < entradaProgramada &&
                  salidaRegistrada > entradaProgramada
                ) {
                  hoursInScheduleForCalc = calcularTiempoTrabajado(
                    entradaProgramada,
                    salidaProgramada
                  );
                }
              }
            }
          }

          let dailyPayment = 0;

          if (correspondingSchedule) {
            let hasRejectedIncidents = false;
            if (correspondingSchedule.incidents) {
              hasRejectedIncidents = correspondingSchedule.incidents.some(
                (incident) =>
                  (incident.status === "Salió tarde" ||
                    incident.status === "Llegó temprano") &&
                  incident.finalStatus === false
              );
            }

            if (hasRejectedIncidents) {
              const hoursToUse = data.NormalCalculationNomina
                ? totalHours
                : hoursInScheduleForCalc > 0
                ? hoursInScheduleForCalc
                : totalHours;
              const normalHours = Math.min(hoursToUse, fullDayHours);
              if (normalHours === fullDayHours) {
                dailyPayment = dailySalary;
              } else {
                const displayHours = Math.floor(normalHours);
                dailyPayment = displayHours * hourlyPay;
              }
            } else {
              const scheduleWithRealTimes = {
                ...correspondingSchedule,
                assistanceCheck_checkTime: checkInTime,
                assistanceCheckout_checkTime: checkOutTime,
              };

              dailyPayment = calculateDailyPayment(
                scheduleWithRealTimes,
                dailySalary,
                hourlyPay,
                data.NormalCalculationNomina || false,
                hoursInScheduleForCalc
              );
            }
          } else {
            const hoursToUse = data.NormalCalculationNomina
              ? totalHours
              : hoursInScheduleForCalc > 0
              ? hoursInScheduleForCalc
              : totalHours;
            const roundedHours = roundHours(hoursToUse, fullDayHours);

            if (roundedHours < fullDayHours) {
              dailyPayment = roundedHours * hourlyPay;
            } else if (roundedHours === fullDayHours) {
              dailyPayment = dailySalary;
            } else if (
              roundedHours > fullDayHours &&
              roundedHours < fullDayHours + 1
            ) {
              dailyPayment = dailySalary;
            } else {
              const extraHours = roundedHours - fullDayHours;
              dailyPayment = dailySalary + extraHours * hourlyPay;
            }
          }

          return dailyPayment;
        })
        .filter(Boolean);

      return payments.reduce((sum, payment) => sum + payment, 0);
    }

    if (data.schedules && data.schedules.length > 0) {
      const dailySalary = Number(String(data.dailySalary ?? "").replace(/[^0-9.-]+/g, ""));
      const hourlyPay = Number(String(data.payPerHour ?? "").replace(/[^0-9.-]+/g, ""));

      const payments = data.schedules.map((schedule) => {
        let hasRejectedIncidents = false;
        if (schedule.incidents) {
          hasRejectedIncidents = schedule.incidents.some(
            (incident) =>
              (incident.status === "Salió tarde" ||
                incident.status === "Llegó temprano") &&
              incident.finalStatus === false
          );
        }

        let totalHours = 0;
        if (
          schedule.assistanceCheck_checkTime &&
          schedule.assistanceCheckout_checkTime
        ) {
          totalHours = calcularTiempoTrabajado(
            schedule.assistanceCheck_checkTime,
            schedule.assistanceCheckout_checkTime
          );
        }

        let hoursInScheduleForCalc = 0;
        if (
          schedule.assistanceCheck_checkTime &&
          schedule.assistanceCheckout_checkTime &&
          schedule.schedule_startTime &&
          schedule.schedule_endTime
        ) {
          const [start] = schedule.schedule_startTime.split(" - ");
          const [, end] = schedule.schedule_endTime.split(" - ");
          const entradaProgramada = start + ":00";
          const salidaProgramada = end + ":00";
          const entradaRegistrada = schedule.assistanceCheck_checkTime;
          const salidaRegistrada = schedule.assistanceCheckout_checkTime;

          if (salidaProgramada === "00:00:00") {
            if (entradaRegistrada !== salidaRegistrada) {
              if (entradaRegistrada >= entradaProgramada) {
                hoursInScheduleForCalc = calcularTiempoTrabajado(
                  entradaRegistrada,
                  "24:00:00"
                );
              } else if (entradaRegistrada < entradaProgramada) {
                hoursInScheduleForCalc = calcularTiempoTrabajado(
                  entradaProgramada,
                  "24:00:00"
                );
              }
            }
          } else {
            if (entradaRegistrada !== salidaRegistrada) {
              if (entradaRegistrada >= entradaProgramada) {
                hoursInScheduleForCalc = calcularTiempoTrabajado(
                  entradaRegistrada,
                  salidaProgramada
                );
              } else if (
                entradaRegistrada < entradaProgramada &&
                salidaRegistrada > entradaProgramada
              ) {
                hoursInScheduleForCalc = calcularTiempoTrabajado(
                  entradaProgramada,
                  salidaProgramada
                );
              }
            }
          }
        }

        if (hasRejectedIncidents) {
          const hoursToUse = data.NormalCalculationNomina
            ? totalHours
            : hoursInScheduleForCalc > 0
            ? hoursInScheduleForCalc
            : totalHours;
          const normalHours = Math.min(hoursToUse, fullDayHours);
          if (normalHours === fullDayHours) {
            return dailySalary;
          } else {
            const displayHours = Math.floor(normalHours);
            return displayHours * hourlyPay;
          }
        } else {
          return calculateDailyPayment(
            schedule,
            dailySalary,
            hourlyPay,
            data.NormalCalculationNomina || false,
            hoursInScheduleForCalc
          );
        }
      });

      return payments.reduce((sum, payment) => sum + payment, 0);
    } else {
      if (data.realSchedule && data.realSchedule.length > 0) {
        const payments = data.realSchedule.map((schedule) => {
          const hoursWorked = Number(schedule.worked);
          const hourlyPay = Number(String(data.payPerHour ?? "").replace(/[^0-9.-]+/g, ""));
          return hoursWorked * hourlyPay;
        });

        return payments.reduce((sum, payment) => sum + payment, 0);
      } else {
        if (data.paymentDetails?.totalPayNumber) {
          return data.paymentDetails.totalPayNumber;
        }

        if (data.totalPayWithIncentives) {
          const totalPayNumber = Number(
            data.totalPayWithIncentives.replace(/[^0-9.-]+/g, "")
          );
          return totalPayNumber;
        }
        return 0;
      }
    }
  }, [
    realCheckRecords,
    dates?.start?.format("YYYY-MM-DD"),
    dates?.end?.format("YYYY-MM-DD"),
    data.partnerId,
    data.schedules,
    data.realSchedule,
    data.dailySalary,
    data.payPerHour,
    data.NormalCalculationNomina,
  ]);

  const getCelebrityDaysTotalFromBreakdown = useMemo(() => {
    if (!data.schedules || data.schedules.length === 0) {
      return 0;
    }

    const dailySalary = Number(String(data.dailySalary ?? "").replace(/[^0-9.-]+/g, ""));
    const hourlyPay = Number(String(data.payPerHour ?? "").replace(/[^0-9.-]+/g, ""));

    let total = 0;
    data.schedules.forEach((schedule) => {
      if (schedule.isCelebrityDayWorked && schedule.celebrityDay) {
        let hoursInScheduleForSchedule = 0;
        if (
          schedule.assistanceCheck_checkTime &&
          schedule.assistanceCheckout_checkTime &&
          schedule.schedule_startTime &&
          schedule.schedule_endTime
        ) {
          const [start] = schedule.schedule_startTime.split(" - ");
          const [, end] = schedule.schedule_endTime.split(" - ");
          const entradaProgramada = start + ":00";
          const salidaProgramada = end + ":00";
          const entradaRegistrada = schedule.assistanceCheck_checkTime;
          const salidaRegistrada = schedule.assistanceCheckout_checkTime;

          if (salidaProgramada === "00:00:00") {
            if (entradaRegistrada !== salidaRegistrada) {
              if (entradaRegistrada >= entradaProgramada) {
                hoursInScheduleForSchedule = calcularTiempoTrabajado(
                  entradaRegistrada,
                  "24:00:00"
                );
              } else if (entradaRegistrada < entradaProgramada) {
                hoursInScheduleForSchedule = calcularTiempoTrabajado(
                  entradaProgramada,
                  "24:00:00"
                );
              }
            }
          } else {
            if (entradaRegistrada !== salidaRegistrada) {
              if (entradaRegistrada >= entradaProgramada) {
                hoursInScheduleForSchedule = calcularTiempoTrabajado(
                  entradaRegistrada,
                  salidaProgramada
                );
              } else if (
                entradaRegistrada < entradaProgramada &&
                salidaRegistrada > entradaProgramada
              ) {
                hoursInScheduleForSchedule = calcularTiempoTrabajado(
                  entradaProgramada,
                  salidaProgramada
                );
              }
            }
          }
        }

        const dailyPayment = calculateDailyPayment(
          schedule,
          dailySalary,
          hourlyPay,
          data.NormalCalculationNomina || false,
          hoursInScheduleForSchedule
        );
        total += dailyPayment;
      }
    });

    return total;
  }, [
    data.schedules,
    data.dailySalary,
    data.payPerHour,
    data.NormalCalculationNomina,
  ]);

  const hourlyPayNumber =
    Number(String(data.payPerHour ?? "").replace(/[^0-9.-]+/g, "")) || 0;
  const extraHourPayTotalNumber = calculateRealExtraHours * hourlyPayNumber;

  const totalPay = useMemo(() => {
    if (useSimpleCalculation) {
      if (
        calculateRealDailyPayments === 0 &&
        (!data.schedules || data.schedules.length === 0) &&
        (!data.realSchedule || data.realSchedule.length === 0)
      ) {
        if (data.totalPayWithIncentives) {
          const totalPayNumber = Number(
            data.totalPayWithIncentives.replace(/[^0-9.-]+/g, "")
          );
          return totalPayNumber;
        }

        if (data.paymentDetails?.totalPayNumber) {
          return (
            data.paymentDetails.totalPayNumber +
            Number(data.totalIncentives?.replace(/[^0-9.-]+/g, "") || 0) -
            Number(data.totalDeductions?.replace(/[^0-9.-]+/g, "") || 0)
          );
        }
      }
      return (
        calculateRealDailyPayments +
        Number(data.totalIncentives?.replace(/[^0-9.-]+/g, "") || 0) -
        Number(data.totalDeductions?.replace(/[^0-9.-]+/g, "") || 0)
      );
    }

    return (
      (calculateRealDailyPayments || 0) +
      Number(
        data.paymentDetails?.freeDaysEnjoyedPay?.replace(/[^0-9.-]+/g, "") || 0
      ) +
      Number(data.totalIncentives?.replace(/[^0-9.-]+/g, "") || 0) -
      Number(data.totalDeductions?.replace(/[^0-9.-]+/g, "") || 0)
    );
  }, [
    useSimpleCalculation,
    calculateRealDailyPayments,
    data.paymentDetails,
    data.totalIncentives,
    data.totalDeductions,
    data.totalPayWithIncentives,
    data.schedules,
    data.realSchedule,
    data.dailySalary,
    data.payPerHour,
  ]);

  return {
    totalPay,
    totalPayFormatted: getCurrencyFormat(totalPay),
    dailyPayments: calculateRealDailyPayments,
    extraHours: calculateRealExtraHours,
    extraHourPay: extraHourPayTotalNumber,
    celebrityDaysTotal: getCelebrityDaysTotalFromBreakdown,
  };
};
